import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '../../../components/Badge';
import { Segmented } from '../../../components/Tabs';
import { CANONICAL_FIELDS } from '@shared/telemetry';
import {
  FLIGHT_MODE_LABELS,
  GPS_FIX_LABELS,
  MISSION_STATE_LABELS,
  TRANSPORT_LABELS,
  formatDuration,
  formatRelativeTime,
} from '@shared/format';
import type { DroneTelemetry } from '@shared/types';
import { useTelemetryAnimationFrame } from '../../../drone/hooks';

interface TelemetryTableProps {
  droneId: string;
  /** store 中的兜底帧 */
  telemetry: DroneTelemetry | null;
  issues: readonly string[];
  className?: string;
}

const GROUPS = Array.from(new Set(CANONICAL_FIELDS.map((field) => field.group)));

/** 这些字段的格式化文本已经自带单位，再拼一次会得到「5 分 11 秒s」。 */
const SELF_UNITED_PATHS = new Set(['battery.remainingSec']);

/**
 * 完整数据列表。
 *
 * 关键指标已经在顶部大数字里展示了，这里承载**所有**规范字段，供排查使用。
 * 更新同样走 rAF 合帧：只改文本节点，不重建表格结构，滚动位置不受影响。
 */
export function TelemetryTable({ droneId, telemetry, issues, className = '' }: TelemetryTableProps) {
  const [group, setGroup] = useState<string>('all');
  const [showRaw, setShowRaw] = useState(false);
  const cellRefs = useRef(new Map<string, HTMLSpanElement>());

  const rows = useMemo(
    () => (group === 'all' ? CANONICAL_FIELDS : CANONICAL_FIELDS.filter((field) => field.group === group)),
    [group],
  );

  useTelemetryAnimationFrame(droneId, (frame) => {
    for (const [path, node] of cellRefs.current) {
      const next = formatPath(frame, path);
      if (node.textContent !== next) node.textContent = next;
    }
  });

  useEffect(() => {
    if (!telemetry) return;
    for (const [path, node] of cellRefs.current) {
      node.textContent = formatPath(telemetry, path);
    }
  }, [telemetry, rows]);

  const rawEntries = telemetry ? Object.entries(telemetry.raw ?? {}) : [];

  return (
    <div className={['flex flex-col gap-2', className].join(' ')}>
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          options={[
            { value: 'all', label: '全部' },
            ...GROUPS.map((name) => ({ value: name, label: name })),
          ]}
          value={group}
          onChange={setGroup}
          size="sm"
        />
        <div className="ml-auto flex items-center gap-1.5">
          {telemetry ? (
            <>
              <Badge tone="neutral">帧序号 {telemetry.seq}</Badge>
              <Badge tone="neutral">接收 {formatRelativeTime(telemetry.receivedAt, Date.now())}</Badge>
              <Badge tone="neutral">{TRANSPORT_LABELS[telemetry.link.transport]}</Badge>
            </>
          ) : (
            <Badge tone="caution">尚未收到遥测帧</Badge>
          )}
        </div>
      </div>

      {issues.length > 0 ? (
        <ul className="flex flex-col gap-1 rounded border border-caution bg-caution-soft px-2.5 py-2">
          {issues.map((issue) => (
            <li key={issue} className="text-xs text-fg">
              解析提示：{issue}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="overflow-hidden rounded border border-line">
        <table className="w-full border-collapse text-xs">
          <tbody>
            {rows.map((field, index) => (
              <tr
                key={field.path}
                className={index % 2 === 0 ? 'bg-surface-1' : 'bg-surface-2'}
              >
                <td className="w-[42%] border-b border-line/60 px-2.5 py-1.5 text-fg-muted">
                  <span className="truncate" title={field.path}>
                    {field.label}
                  </span>
                  <span className="ml-1.5 hidden font-mono text-[10px] text-fg-subtle xl:inline">
                    {field.path}
                  </span>
                </td>
                <td className="border-b border-line/60 px-2.5 py-1.5 text-right font-semibold text-fg">
                  <span
                    ref={(node) => {
                      if (node) cellRefs.current.set(field.path, node);
                      else cellRefs.current.delete(field.path);
                    }}
                    className="tabular"
                  >
                    {formatPath(telemetry, field.path)}
                  </span>
                  {field.unit && !SELF_UNITED_PATHS.has(field.path) ? (
                    <span className="ml-1 text-fg-subtle">{field.unit}</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rawEntries.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => setShowRaw((value) => !value)}
            className="self-start text-xs font-medium text-accent hover:underline"
          >
            {showRaw ? '收起' : '展开'}原始未映射字段（{rawEntries.length}）
          </button>
          {showRaw ? (
            <div className="max-h-56 overflow-auto rounded border border-line bg-surface-2 p-2.5">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-1 xl:grid-cols-2">
                {rawEntries.map(([key, value]) => (
                  <div key={key} className="flex items-baseline gap-2 text-[11px]">
                    <dt className="font-mono text-fg-muted">{key}</dt>
                    <dd className="tabular ml-auto truncate text-fg" title={stringify(value)}>
                      {stringify(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function formatPath(telemetry: DroneTelemetry | null, path: string): string {
  const value = telemetry ? readPath(telemetry, path) : undefined;
  if (value === undefined || value === null) return '--';
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (typeof value === 'string') {
    if (path === 'flight.mode') {
      return FLIGHT_MODE_LABELS[value as keyof typeof FLIGHT_MODE_LABELS] ?? value;
    }
    if (path === 'flight.missionState') {
      return MISSION_STATE_LABELS[value as keyof typeof MISSION_STATE_LABELS] ?? value;
    }
    if (path === 'gps.fixType') {
      return GPS_FIX_LABELS[value as keyof typeof GPS_FIX_LABELS] ?? value;
    }
    return value;
  }
  if (typeof value === 'number') {
    if (path === 'timestamp') return `${value}（${formatRelativeTime(value, Date.now())}）`;
    if (path === 'battery.remainingSec') return formatDuration(value);
    if (path === 'position.lat' || path === 'position.lng') return value.toFixed(6);
    const digits = Number.isInteger(value) ? 0 : Math.abs(value) >= 100 ? 1 : 2;
    return value.toFixed(digits);
  }
  if (Array.isArray(value)) return `${value.length} 项`;
  return '--';
}

function readPath(source: unknown, path: string): unknown {
  let current: unknown = source;
  for (const segment of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function stringify(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
